import { CheckCircleFilled } from '@ant-design/icons';
import {
    Button,
    Checkbox,
    Flex,
    Input,
    Select,
    Space,
    Tooltip,
    Typography,
} from 'antd';
import { DateTime } from 'luxon';
import { PropsWithChildren, useState } from 'react';

import {
    AuditEntityType,
    CertificationDto,
    CertificationVerificationStatus,
    InfectiousDiseaseTestType,
    ListProfileDto,
    MaskType,
    NurseLicenseDto,
    VaccinationDto,
} from '@clh/api-client';
import { MASK_TYPE_DISPLAY_NAMES } from '@clh/ui';
import { HumlaDateTime, TuberculosisWorkHistoryQuestions } from '@clh/util';

import { ActionButton } from '../../action-button';
import AuditHistoryButton from '../../audit-history/button';
import { useApiCache } from '../../hooks/use-api-cache';
import { ApiClient, useApiClient } from '../../hooks/use-api-client';
import { useSelection } from '../../hooks/use-selection';
import NurseStatePracticeAuthorizationsButton from '../../nurse-state-practice-authorizations/button';
import ObjectPropertyList from '../../record-details/object-property-list';
import { FetchRecordTable } from '../../table/table';

const Title = ({ children }: PropsWithChildren) => (
    <Typography.Title level={4}>{children}</Typography.Title>
);

interface SectionProps {
    api: ApiClient;
    profileId: string;
}

const EditCertificationVerificationStatus = ({
    code,
    profileId,
    currentStatus,
    verificationDate,
}: {
    code: string;
    profileId: string;
    currentStatus: CertificationVerificationStatus;
    verificationDate?: Date | null;
}) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [verificationStatus, setVerificationStatus] =
        useState<CertificationVerificationStatus>();
    const api = useApiClient();

    if (!isEditing) {
        return (
            <>
                {currentStatus === CertificationVerificationStatus.Verified &&
                verificationDate ? (
                    <Tooltip
                        title={`Verified on ${DateTime.fromJSDate(
                            verificationDate
                        ).toLocaleString(HumlaDateTime.DATETIME_SHORT)}`}
                    >
                        {currentStatus}
                    </Tooltip>
                ) : (
                    currentStatus
                )}{' '}
                <a onClick={() => setIsEditing(!isEditing)}>Change</a>
            </>
        );
    }

    return (
        <>
            <Select
                style={{ width: '50%' }}
                options={Object.entries(CertificationVerificationStatus).map(
                    (vals) => ({ label: vals[0], value: vals[1] })
                )}
                defaultValue={currentStatus}
                onChange={(status: CertificationVerificationStatus) => {
                    setVerificationStatus(status);
                }}
            />
            <ActionButton
                action={async () => {
                    if (verificationStatus) {
                        await api?.certificationControllerSetVerificationStatus(
                            {
                                profileId,
                                code,
                                status: verificationStatus,
                            }
                        );
                        setIsEditing(false);
                    }
                }}
                disabled={!verificationStatus}
                successMessage="Certification verification status updated"
            >
                Save
            </ActionButton>
        </>
    );
};

const Certifications = ({ api, profileId }: SectionProps) => {
    const { selection } = useSelection<ListProfileDto>();

    return (
        <div>
            <Title>Certifications</Title>
            <FetchRecordTable
                size="small"
                rowKey="code"
                pagination={false}
                getRecords={() =>
                    api
                        .certificationControllerGetCertifications({
                            profileId,
                        })
                        .then((c) => c.filter((c) => !!c.expiration))
                }
                cacheKey={
                    'certificationControllerGetCertifications' + profileId
                }
                columns={[
                    'code',
                    'expiration',
                    {
                        title: 'Card',
                        dataIndex: 'document',
                        render: (card) =>
                            card ? (
                                <a
                                    href={card.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Download
                                </a>
                            ) : (
                                <i>N/A</i>
                            ),
                    },
                    {
                        title: 'Status',
                        dataIndex: 'verificationStatus',
                        render: (
                            status: CertificationVerificationStatus,
                            row: CertificationDto
                        ) => {
                            if (row.ahaECardCode || row.rqiCertificateCode) {
                                return (
                                    <>
                                        {row.ahaECardCode ? 'AHA eCard' : 'RQI'}{' '}
                                        Verified
                                        {row.holderName !==
                                        `${selection.firstName} ${selection.lastName}` ? (
                                            <>
                                                <br />
                                                <CheckCircleFilled
                                                    style={{
                                                        color: 'green',
                                                    }}
                                                />{' '}
                                                Nurse confirmed name mismatch
                                            </>
                                        ) : null}
                                    </>
                                );
                            } else if (row.verificationStatus) {
                                return (
                                    <EditCertificationVerificationStatus
                                        profileId={profileId}
                                        code={row.code}
                                        currentStatus={status}
                                        verificationDate={row.verificationDate}
                                    />
                                );
                            }

                            return 'N/A';
                        },
                    },
                ]}
            />
        </div>
    );
};

const Contracts = ({ api, profileId }: SectionProps) => {
    const { record: confidentiality, setRecord: setConfidentiality } =
        useApiCache(
            () =>
                api.nurseControllerGetConfidentialityAgreement({
                    profileId,
                }),
            'nurseControllerGetConfidentialityAgreement' + profileId
        );
    const { record: contract, setRecord: setContract } = useApiCache(
        () =>
            api.nurseControllerGetContract({
                profileId,
            }),
        'nurseControllerGetContract' + profileId
    );

    const fields = {
        signedAt: true as const,
        signature: (sig: string) => (
            <>{sig && <img src={sig} style={{ width: 220 }} />}</>
        ),
    };

    return (
        <div>
            <Title>Contracts</Title>
            <b>Offer Letter Agreement</b>
            {contract?.signedAt ? (
                <div style={{ marginBottom: 10 }}>
                    <ObjectPropertyList values={contract} fields={fields} />
                    <ActionButton
                        successMessage="Offer letter reset"
                        action={async () => {
                            await api.nurseControllerResetContract({
                                profileId,
                            });
                            setContract((x) =>
                                x
                                    ? {
                                          ...x,
                                          signedAt: null,
                                          signature: null,
                                      }
                                    : x
                            );
                        }}
                    >
                        Reset Offer Letter
                    </ActionButton>
                </div>
            ) : (
                <p>
                    <i>Not yet signed</i>
                </p>
            )}
            <b>Confidentiality Agreement</b>
            {confidentiality?.signedAt ? (
                <div>
                    <ObjectPropertyList
                        values={confidentiality}
                        fields={fields}
                    />
                    <ActionButton
                        successMessage="Confidentiality agreement reset"
                        action={async () => {
                            await api.nurseControllerResetConfidentialityAgreement(
                                {
                                    profileId,
                                }
                            );
                            setConfidentiality((x) =>
                                x
                                    ? {
                                          ...x,
                                          signedAt: null,
                                          signature: null,
                                      }
                                    : x
                            );
                        }}
                    >
                        Reset Confidentiality Agreement
                    </ActionButton>
                </div>
            ) : (
                <p>
                    <i>Not yet signed</i>
                </p>
            )}
        </div>
    );
};

const Licenses = ({ api, profileId }: SectionProps) => {
    const licenseFormDefaultState = {
        licenseNumber: '',
        jurisdiction: '',
        licenseType: 'RN' as const,
        skipFormatting: false,
    };
    const [licenseToAdd, setLicenseToAdd] = useState<{
        licenseNumber: string;
        jurisdiction: string;
        licenseType: 'RN';
        skipFormatting: boolean;
    }>({ ...licenseFormDefaultState });

    const [isAddingLicense, setIsAddingLicense] = useState(false);

    return (
        <div>
            <Flex justify="space-between" align="flex-end">
                <Title>Licenses</Title>

                <NurseStatePracticeAuthorizationsButton
                    nurseProfileId={profileId}
                />
            </Flex>
            <FetchRecordTable
                size="small"
                pagination={false}
                getRecords={() =>
                    api.nurseControllerGetLicenses({
                        profileId,
                    })
                }
                cacheKey={'nurseControllerGetLicenses' + profileId}
                columns={[
                    {
                        title: 'License #',
                        dataIndex: 'licenseNumber',
                        render: (
                            licenseNumber: number,
                            license: NurseLicenseDto
                        ) => {
                            return (
                                <AuditHistoryButton
                                    entityId={license.id}
                                    entityType={AuditEntityType.License}
                                >
                                    {licenseNumber}
                                </AuditHistoryButton>
                            );
                        },
                    },
                    {
                        title: 'Expiration',
                        render: (license: NurseLicenseDto) => {
                            if (!license.expirationDate) return null;
                            const dt = DateTime.fromJSDate(
                                license.expirationDate
                            );
                            const isExpired = dt < DateTime.now();
                            return (
                                <span
                                    style={
                                        isExpired ? { color: 'red' } : undefined
                                    }
                                >
                                    {dt.toLocaleString(DateTime.DATE_SHORT)}
                                </span>
                            );
                        },
                    },
                    'jurisdiction',
                    {
                        title: 'Compact',
                        dataIndex: 'compactStatus',
                        render: (
                            compactStatus: NurseLicenseDto['compactStatus']
                        ) => {
                            if (!compactStatus) return null;
                            return compactStatus === 'MULTI_STATE'
                                ? 'Multi-State'
                                : 'Single State';
                        },
                    },
                    {
                        title: 'Status',
                        render: (license: NurseLicenseDto) => (
                            <Space>
                                {license.status}

                                <ActionButton
                                    successMessage="License reverification submitted"
                                    confirm="Are you sure you want to reverify this license? This will submit a new verification request to Nursys."
                                    action={async () => {
                                        await api.nurseControllerReverifyLicense(
                                            {
                                                licenseId: license.id,
                                                profileId:
                                                    license.nurseProfileId,
                                            }
                                        );
                                    }}
                                    size="small"
                                >
                                    Reverify
                                </ActionButton>

                                <ActionButton
                                    successMessage="License deleted"
                                    confirm="Are you sure you want to delete this license?"
                                    action={async () => {
                                        await api.nurseControllerDeleteLicense({
                                            licenseId: license.id,
                                            profileId: license.nurseProfileId,
                                        });
                                    }}
                                    size="small"
                                >
                                    Delete
                                </ActionButton>
                            </Space>
                        ),
                    },
                ]}
            >
                <Space.Compact style={{ marginTop: 10 }}>
                    {isAddingLicense ? (
                        <>
                            <Input
                                maxLength={4}
                                width={100}
                                value={licenseToAdd.jurisdiction}
                                addonBefore="Jurisdiction"
                                onChange={(input) =>
                                    setLicenseToAdd({
                                        ...licenseToAdd,
                                        jurisdiction:
                                            input.currentTarget.value.toUpperCase(),
                                    })
                                }
                            />
                            <Input
                                addonBefore="License #"
                                value={licenseToAdd.licenseNumber}
                                onChange={(input) =>
                                    setLicenseToAdd({
                                        ...licenseToAdd,
                                        licenseNumber:
                                            input.currentTarget.value,
                                    })
                                }
                            />
                            <ActionButton
                                action={async () => {
                                    await api.nurseControllerCreateLicense({
                                        profileId,
                                        createNurseLicensesDto: {
                                            licenses: [licenseToAdd],
                                        },
                                    });

                                    setLicenseToAdd({
                                        ...licenseFormDefaultState,
                                    });
                                    setIsAddingLicense(false);
                                }}
                                disabled={
                                    !licenseToAdd.jurisdiction ||
                                    !licenseToAdd.licenseNumber ||
                                    !licenseToAdd.licenseType
                                }
                                successMessage="License submitted"
                            >
                                Submit
                            </ActionButton>
                            <Button
                                onClick={() => {
                                    setLicenseToAdd({
                                        ...licenseFormDefaultState,
                                    });
                                    setIsAddingLicense(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => setIsAddingLicense(true)}>
                            Add New License
                        </Button>
                    )}
                </Space.Compact>
                {isAddingLicense && (
                    <Checkbox
                        style={{ display: 'flex', marginTop: 8 }}
                        checked={licenseToAdd.skipFormatting}
                        onChange={(e) =>
                            setLicenseToAdd({
                                ...licenseToAdd,
                                skipFormatting: e.target.checked,
                            })
                        }
                    >
                        Use exactly as entered
                    </Checkbox>
                )}
            </FetchRecordTable>
        </div>
    );
};

const Vaccinations = ({ api, profileId }: SectionProps) => (
    <div>
        <Title>Immunizations</Title>
        <FetchRecordTable
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            getRecords={() =>
                api.vaccinationControllerGetVaccinations({
                    profileId,
                })
            }
            cacheKey={'vaccinationControllerGetVaccinations' + profileId}
            columns={[
                'code',
                'declinationDate',
                'expiresAt',
                {
                    title: 'Card',
                    dataIndex: 'document',
                    render: (document: VaccinationDto['document']) =>
                        document ? (
                            <a
                                href={document}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download
                            </a>
                        ) : (
                            <i>N/A</i>
                        ),
                },
                {
                    title: 'Titer Date',
                    render: (v: VaccinationDto) =>
                        v.hasTiter ? (
                            DateTime.fromJSDate(v.doses[0].date).toFormat(
                                'MM/dd/yyyy'
                            )
                        ) : (
                            <i>N/A</i>
                        ),
                },
                {
                    title: 'Doses',
                    render: (v: VaccinationDto) => (
                        <>
                            {!v.hasTiter && v.doses.length ? (
                                <ol
                                    style={{
                                        marginBottom: 0,
                                        paddingLeft: 12,
                                    }}
                                >
                                    {v.doses.map((dose, i) => (
                                        <li
                                            key={`${i}-${dose.manufacturer}-${dose.date}`}
                                        >
                                            Date:{' '}
                                            {DateTime.fromJSDate(
                                                dose.date
                                            ).toFormat('MM/dd/yyyy')}
                                            {dose.manufacturer && (
                                                <>
                                                    <br />
                                                    Manufacturer:{' '}
                                                    {dose.manufacturer}
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <i>N/A</i>
                            )}
                        </>
                    ),
                },
            ]}
        />
    </div>
);

const TuberculosisTest = ({ api, profileId }: SectionProps) => {
    const { record: tbTest } = useApiCache(
        () =>
            api.infectiousDiseaseTestControllerGetTest({
                code: InfectiousDiseaseTestType.Mantoux,
                profileId,
            }),
        'infectiousDiseaseTestControllerGetTest' + profileId
    );

    const {
        record: tbTestOrder,
        clearCache: invalidateTbTestOrderCache,
        setRecord: setTbTestOrder,
    } = useApiCache(
        () =>
            api.infectiousDiseaseTestControllerGetTestOrder({
                profileId,
            }),
        'infectiousDiseaseTestControllerGetTestOrder' + profileId
    );

    const { record: tbScreening } = useApiCache(
        () =>
            api.tuberculosisScreeningControllerGetResponses({
                profileId,
            }),
        'tuberculosisScreeningControllerGetResponses' + profileId
    );

    const didNotShowSymptomsInScreening =
        tbScreening?.symptoms.every((s) => !s.answer) &&
        tbScreening?.workHistoryQuestions.every((q) => !q.answer);

    const exposureDate = tbScreening?.workHistoryQuestions.find(
        (w) => w.code === TuberculosisWorkHistoryQuestions.CLOSE_CONTACT
    )?.date;

    const testHasValidDate =
        tbTest &&
        (exposureDate
            ? tbTest.testDate > exposureDate
            : tbTest.testDate > DateTime.now().minus({ year: 1 }).toJSDate());

    const isPending = tbTestOrder && !tbTestOrder.orderedAt;

    return (
        <div>
            <Title>Mantoux or QuantiFERON Gold</Title>

            <Typography.Title level={5}>TB Test Order</Typography.Title>
            {tbTestOrder ? (
                <div style={{ marginBottom: '24px' }}>
                    <ObjectPropertyList
                        values={{
                            requestedDate: tbTestOrder.createdAt,
                            status: isPending ? 'Pending' : 'Ordered',
                            orderedDate: tbTestOrder.orderedAt ?? 'N/A',
                            orderedBy: tbTestOrder.orderedBy,
                        }}
                        fields={{
                            requestedDate: true,
                            status: true,
                            orderedDate: true,
                            orderedBy: true,
                        }}
                    />
                    {isPending && (
                        <ActionButton
                            type="primary"
                            style={{ marginTop: '12px' }}
                            action={async () => {
                                const order =
                                    await api.infectiousDiseaseTestControllerFulfillTestOrder(
                                        {
                                            profileId,
                                            orderId: tbTestOrder.id,
                                        }
                                    );
                                invalidateTbTestOrderCache();
                                setTbTestOrder((x) =>
                                    x
                                        ? {
                                              ...x,
                                              orderedAt: new Date(),
                                              orderedBy: order.orderedBy,
                                          }
                                        : x
                                );
                            }}
                            successMessage="Test has been marked as ordered"
                        >
                            Mark as Ordered
                        </ActionButton>
                    )}
                </div>
            ) : (
                <i>(none)</i>
            )}

            <Typography.Title level={5}>TB Test Result</Typography.Title>
            {tbTest ? (
                <ObjectPropertyList
                    values={{
                        ...tbTest,
                        testDocument: tbTest.testCardV2?.href,
                        requirement: tbScreening
                            ? didNotShowSymptomsInScreening
                                ? 'Not required based on screening answers'
                                : tbTest.isTestDateValid
                                  ? 'Required based on screening answers'
                                  : 'Test is expired'
                            : 'None',
                    }}
                    fields={{
                        testDate: true,
                        testDocument: (testDocument) =>
                            testDocument && (
                                <a
                                    href={testDocument}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    View Test
                                </a>
                            ),
                        requirement: true,
                    }}
                />
            ) : (
                <ObjectPropertyList
                    values={{
                        testDate:
                            tbScreening && testHasValidDate
                                ? didNotShowSymptomsInScreening
                                    ? 'Not required based on screening answers'
                                    : 'Required based on screening answers'
                                : 'None',
                        testDocument: 'No Document',
                    }}
                    fields={{
                        testDate: true,
                        testDocument: true,
                    }}
                />
            )}
        </div>
    );
};

const FitTest = ({ api, profileId }: SectionProps) => {
    const { record: fitTest } = useApiCache(
        () => api.fitTestControllerGetFitTest({ profileId }),
        'fitTestControllerGetFitTest' + profileId
    );

    return (
        <div>
            <Title>N95 Respirator Fit Test</Title>

            {fitTest ? (
                <ObjectPropertyList
                    values={{
                        ...fitTest,
                        maskType:
                            fitTest.maskType === MaskType.Other
                                ? (fitTest.otherDescription ?? 'Other')
                                : MASK_TYPE_DISPLAY_NAMES[fitTest.maskType],
                        // testDate is a @db.Date column, so it arrives as midnight UTC —
                        // rendering it in local time slips it back a day west of UTC.
                        testDate: DateTime.fromJSDate(fitTest.testDate, {
                            zone: 'utc',
                        }).toFormat('MM/dd/yyyy'),
                        documentation: fitTest.documentation?.href ?? null,
                    }}
                    fields={{
                        maskType: true,
                        testDate: true,
                        documentation: (documentation) =>
                            documentation ? (
                                <a
                                    href={documentation}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Download
                                </a>
                            ) : (
                                <i>N/A</i>
                            ),
                    }}
                />
            ) : (
                <i>(none)</i>
            )}
        </div>
    );
};

const TuberculosisScreening = ({ api, profileId }: SectionProps) => {
    const { record: tbScreening } = useApiCache(
        () =>
            api.tuberculosisScreeningControllerGetResponses({
                profileId,
            }),
        'tuberculosisScreeningControllerGetResponses' + profileId
    );

    return (
        <div>
            <Title>Annual Tuberculosis Screening</Title>

            {tbScreening ? (
                <ObjectPropertyList
                    values={{
                        ...tbScreening,
                        symptoms: tbScreening.symptoms.map(
                            (s) => s.name + ': ' + (s.answer ? 'Yes' : 'No')
                        ),
                        workHistoryQuestions:
                            tbScreening.workHistoryQuestions.map(
                                (w) =>
                                    w.question +
                                    (w.subtext ? ' ' + w.subtext : '') +
                                    ': ' +
                                    (w.answer ? 'Yes' : 'No') +
                                    (w.date
                                        ? ' on ' +
                                          DateTime.fromJSDate(w.date).toFormat(
                                              'MM/dd/yyyy'
                                          )
                                        : '')
                            ),
                        expirationDate: tbScreening.expiresAt,
                    }}
                    fields={{
                        screeningDate: true,
                        expirationDate: true,
                        symptoms: true,
                        workHistoryQuestions: true,
                        signature: (sig: string) => (
                            <>
                                {sig && (
                                    <img src={sig} style={{ width: 220 }} />
                                )}
                            </>
                        ),
                    }}
                />
            ) : (
                <i>(none)</i>
            )}
        </div>
    );
};

export default function NurseProfileTab() {
    const api = useApiClient();
    const { selection } = useSelection<ListProfileDto>();

    if (!api) {
        return null;
    }

    const props = { api, profileId: selection.id };

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Licenses {...props} />
            <Certifications {...props} />
            <Contracts {...props} />
            <Vaccinations {...props} />
            <TuberculosisScreening {...props} />
            <TuberculosisTest {...props} />
            <FitTest {...props} />
        </Space>
    );
}
